import React, { useEffect, useRef } from 'react';

const WaterBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationId;
    let droplets = [];
    let ripples = [];

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Drop class
    class Drop {
      constructor() {
        this.reset();
      }

      reset() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * -canvas.height - 20;
        this.radius = 1.5 + Math.random() * 3.5;
        this.speed = 1 + Math.random() * 3;
        this.opacity = 0.4 + Math.random() * 0.4;
        this.wobble = Math.random() * Math.PI * 2;
        this.wobbleSpeed = 0.02 + Math.random() * 0.03;
        this.wobbleAmount = 0.3 + Math.random() * 0.7;
        this.elongation = 0.8 + Math.random() * 0.4;
        this.trail = [];
        this.maxTrail = 3 + Math.random() * 5;
      }

      update() {
        this.y += this.speed;
        this.wobble += this.wobbleSpeed;
        this.x += Math.sin(this.wobble) * this.wobbleAmount;

        // Add trail
        this.trail.push({ x: this.x, y: this.y });
        if (this.trail.length > this.maxTrail) {
          this.trail.shift();
        }

        // Reset if out of bounds
        if (this.y > canvas.height + 50) {
          // Create ripple on impact
          ripples.push(new Ripple(this.x, canvas.height - 10, this.radius));
          this.reset();
        }
      }

      draw(ctx) {
        // Draw trail
        for (let i = 0; i < this.trail.length - 1; i++) {
          const alpha = (i / this.trail.length) * this.opacity * 0.2;
          const radius = this.radius * (0.3 + (i / this.trail.length) * 0.7);
          ctx.beginPath();
          ctx.arc(this.trail[i].x, this.trail[i].y, radius, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
          ctx.fill();
        }

        // Draw main drop with glow
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.scale(1, this.elongation);

        // Glow effect
        const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, this.radius * 2.5);
        gradient.addColorStop(0, `rgba(255, 255, 255, ${this.opacity})`);
        gradient.addColorStop(0.4, `rgba(200, 230, 255, ${this.opacity * 0.5})`);
        gradient.addColorStop(1, `rgba(14, 165, 233, ${this.opacity * 0.05})`);

        ctx.beginPath();
        ctx.arc(0, 0, this.radius, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();

        // Highlight on top of drop
        ctx.beginPath();
        ctx.arc(-this.radius * 0.3, -this.radius * 0.3, this.radius * 0.25, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${this.opacity * 0.7})`;
        ctx.fill();

        ctx.restore();
      }
    }

    // Ripple class
    class Ripple {
      constructor(x, y, dropSize) {
        this.x = x;
        this.y = y;
        this.radius = 2;
        this.maxRadius = 30 + dropSize * 10;
        this.opacity = 0.5;
        this.speed = 0.5 + dropSize * 0.15;
        this.life = 1;
        this.decay = 0.01 + dropSize * 0.002;
        this.waves = 2 + Math.floor(dropSize);
      }

      update() {
        this.radius += this.speed;
        this.opacity *= (1 - this.decay);
        this.life -= this.decay;
        return this.life > 0;
      }

      draw(ctx) {
        for (let i = 0; i < this.waves; i++) {
          const radius = this.radius - i * 6;
          if (radius > 0) {
            const alpha = this.opacity * (1 - i / this.waves);
            
            // Outer ring
            ctx.beginPath();
            ctx.arc(this.x, this.y, radius, 0, Math.PI * 2);
            ctx.strokeStyle = `rgba(255, 255, 255, ${alpha * 0.4})`;
            ctx.lineWidth = 1.2 - i * 0.2;
            ctx.stroke();

            // Inner glow
            if (i === 0) {
              const gradient = ctx.createRadialGradient(
                this.x, this.y, 0,
                this.x, this.y, radius
              );
              gradient.addColorStop(0, `rgba(255, 255, 255, ${alpha * 0.05})`);
              gradient.addColorStop(0.5, `rgba(255, 255, 255, ${alpha * 0.02})`);
              gradient.addColorStop(1, `rgba(255, 255, 255, 0)`);
              ctx.beginPath();
              ctx.arc(this.x, this.y, radius, 0, Math.PI * 2);
              ctx.fillStyle = gradient;
              ctx.fill();
            }

            // Second inner ring for more detail
            if (i === 0 && radius > 10) {
              ctx.beginPath();
              ctx.arc(this.x, this.y, radius * 0.6, 0, Math.PI * 2);
              ctx.strokeStyle = `rgba(255, 255, 255, ${alpha * 0.15})`;
              ctx.lineWidth = 0.5;
              ctx.stroke();
            }
          }
        }
      }
    }

    // Create initial droplets
    const createDroplets = (count) => {
      for (let i = 0; i < count; i++) {
        const drop = new Drop();
        drop.y = Math.random() * canvas.height;
        droplets.push(drop);
      }
    };
    createDroplets(40);

    // Animation loop
    const animate = () => {
      // Clear canvas with transparent background
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update and draw droplets
      droplets.forEach(drop => {
        drop.update();
        drop.draw(ctx);
      });

      // Update and draw ripples
      ripples = ripples.filter(ripple => {
        const alive = ripple.update();
        if (alive) ripple.draw(ctx);
        return alive;
      });

      // Add new droplets periodically
      if (Math.random() < 0.03 && droplets.length < 60) {
        droplets.push(new Drop());
      }

      // Add random ripples for natural effect
      if (Math.random() < 0.003) {
        const x = 20 + Math.random() * (canvas.width - 40);
        const y = canvas.height - 10 - Math.random() * 20;
        const size = 1 + Math.random() * 3;
        ripples.push(new Ripple(x, y, size));
      }

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      zIndex: -2,
      pointerEvents: 'none',
      overflow: 'hidden',
      background: 'linear-gradient(180deg, #e8f4f8 0%, #d4ecf5 40%, #c0e4f0 70%, #a8d8e8 100%)'
    }}>
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none'
        }}
      />

      {/* CSS Sparkle Effects */}
      <style>{`
        @keyframes sparkle {
          0%, 100% { opacity: 0; transform: scale(0.3); }
          50% { opacity: 0.6; transform: scale(1); }
        }

        .sparkle {
          position: absolute;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(255,255,255,0.6), rgba(255,255,255,0));
          animation: sparkle 4s ease-in-out infinite;
          pointer-events: none;
        }
      `}</style>

      {/* Sparkle effects */}
      {[...Array(20)].map((_, i) => (
        <div
          key={i}
          className="sparkle"
          style={{
            left: `${Math.random() * 95 + 2.5}%`,
            top: `${Math.random() * 95 + 2.5}%`,
            width: `${2 + Math.random() * 3}px`,
            height: `${2 + Math.random() * 3}px`,
            animationDelay: `${Math.random() * 6}s`,
            animationDuration: `${3 + Math.random() * 4}s`,
            opacity: 0.2 + Math.random() * 0.3
          }}
        />
      ))}
    </div>
  );
};

export default WaterBackground;